/**
 * Words and paths for a decision's record — shared by its page
 * (/prioritization/decisions/:decisionId), the Decisions app's screen and
 * the decision log's map. Not a component file, so fast refresh stays whole.
 */
import type { Decision } from '@/types/prioritization/decisions';

/** The canonical URL of one decision: its own page. */
export const decisionPagePath = (id: string) => `/prioritization/decisions/${id}`;

/**
 * A decision's date in words. A date with no time ("2026-10-31", a review
 * date) is that calendar day everywhere: it is never read as midnight UTC,
 * which shows as the day before in any timezone west of Greenwich.
 */
export function formatDecisionDate(iso: string): string {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(iso);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * The value a date input shows for a stored moment: the calendar day it fell
 * on where the reader is, not the UTC day (which is tomorrow every evening
 * west of Greenwich).
 */
export function decisionDateInputValue(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The moment to store for a day picked in a date input: noon that day, local
 * time. Midnight UTC — what `new Date('2026-10-08')` gives — is the evening
 * before across the Americas, so the saved decision showed the wrong day.
 */
export function decisionDateInputToIso(value: string): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0).toISOString();
}

/** Humanize an auto-decision's source event type ("pool_published" → "pool published"). */
function humanizeSource(source: string): string {
  return source.replaceAll('_', ' ');
}

/** The line under the title: who recorded it, or what recorded it. */
export function decisionSubtitle(decision: Decision): string {
  return decision.decisionKind === 'auto'
    ? `Recorded automatically${decision.source ? ` when a ${humanizeSource(decision.source)} was memorialized` : ''}.`
    : `Entered by ${decision.decidedByName ?? 'a workspace member'}.`;
}
